import { i18n } from '@lingui/core';
import { getCookie } from '@mindbaz/app-manager';
import { Button, Text } from '@mindbaz/ui-v2';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import TitleDescriptionBlock from 'components/common/TitleDescriptionBlock';
import { getDashboardsAccess } from 'packages/endpoints/access';
import { getPerson } from 'packages/endpoints/common';
import getCustomDashboards from 'packages/endpoints/dashboards';

import Card from '../../components/common/Cards/Card';
import StyledNav from './styledComponents';
import { TCards, TCustomDashboards } from './types';

const Dashboards = () => {
  const { data: dashboardsAccess } = useQuery(['dashboards-access'], () =>
    getDashboardsAccess()
  );

  if (!dashboardsAccess.userHasAccess) {
    window.location.replace(`${import.meta.env.VITE_RUBIXO_URL}accessdenied`);
  }

  const { data: person } = useQuery(['get-person'], () => getPerson());
  const { data: customDashboards } = useQuery(
    ['getDatabases'],
    () => person && getCustomDashboards(person.idCompany)
  );

  const CARD_TYPE: TCards[] = useMemo(
    () => [
      {
        typeName: 'deliverabilityReport',
        link: import.meta.env.VITE_DELIVERABILITY_IMPORT_LINK || '',
        content: i18n._('dashboards.menuCards.deliverability.content'),
        titleIcon: 'mark_email_read',
        titleText: i18n._('dashboards.menuCards.deliverability.title'),
      },
      {
        typeName: 'campaignStatistics',
        link: import.meta.env.VITE_CAMPAIGN_STATISTIC_LINK || '',
        content: i18n._('dashboards.menuCards.campaign.content'),
        titleIcon: 'insert_chart',
        titleText: i18n._('dashboards.menuCards.campaign.title'),
      },
    ],
    []
  );

  const handleCardClick = useCallback((link: string) => {
    const token = getCookie('at');
    window.location.replace(`${link}${token}`);
  }, []);

  return (
    <>
      <TitleDescriptionBlock
        title={i18n._("dashboards.title")}
        description={i18n._("dashboards.description")}
      />
      <StyledNav>
        {CARD_TYPE.map(
          ({ typeName, content, titleIcon, titleText, link }: TCards) => (
            <div key={typeName} onClick={() => handleCardClick(link)}>
              <Card
                content={content}
                titleIcon={titleIcon}
                titleText={titleText}
              />
            </div>
          )
        )}
      </StyledNav>
      <Text variant="h2" weight="bold">{i18n._('dashboards.customDashboards')}</Text>
      {customDashboards && customDashboards.length > 0 ? (
        <StyledNav>
          {customDashboards.map(
            ({ idCustomDashboard, name, dashboardUrl }: TCustomDashboards) => (
              <div
                key={idCustomDashboard}
                onClick={() =>
                  handleCardClick(
                    `${import.meta.env.VITE_CUSTOM_LINK}${dashboardUrl}&token=`
                  )
                }
              >
                <Card
                  titleIcon="dashboard_customize"
                  titleText={name.charAt(0).toUpperCase() + name.slice(1)}
                />
              </div>
            )
          )}
        </StyledNav>
      ) : (
        <StyledNav
          onClick={() => {
            window.location.href =
              'mailto:serviceclient@mindbaz?subject=custom%20dashboards';
          }}
        >
          <Card
            titleIcon="dashboard_customize"
            titleText={i18n._('dashboards.needCustomDashboard')}
            button={<Button>Contact Us</Button>}
          />
        </StyledNav>
      )}
    </>
  );
};
export default Dashboards;
